import { useCallback } from "react";
import { FieldValues } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { IVerticalTabProps } from "@miplatform/types";
import { usePermissions } from "@mibuildweb/hooks";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { VerticalTabStatusIcon } from "./VerticalTabStatusIcon";

export function VerticalTabsSelector<GenericData extends FieldValues>(
  props: IVerticalTabSelectorProps<GenericData>
) {
  const {
    tabs,
    selectedTab,
    tabTitleGetter,
    tabSubtitleGetter,
    onTabSelect,
    hideStatusIcons = false,
    useLegacyStyles = false,
  } = props;

  const { classes } = useStyles({ useLegacyStyles });

  const handleTabSelect = useCallback(
    (item: IVerticalTabProps<GenericData> | null) => {
      if (onTabSelect) {
        if (
          !item?.controlledFormViewComponent &&
          item?.subTabs &&
          item.subTabs.length > 0
        ) {
          onTabSelect(item?.subTabs[0]);
          return;
        }
        onTabSelect(item);
      }
    },
    [onTabSelect]
  );

  const isSelectedTabValue = (tab: IVerticalTabProps<GenericData>) => {
    if (useLegacyStyles) {
      return selectedTab === tab.value;
    }

    return selectedTab === tab.value;
  };

  return (
    <Box className={classes.filterContainer}>
      <List className={classes.list}>
        {tabs.map((item) => (
          <div key={item.value}>
            <TabSelectorItem
              item={item}
              onSelect={handleTabSelect}
              selected={isSelectedTabValue(item)}
              disabled={item.disabled}
              tabTitleGetter={tabTitleGetter}
              tabSubtitleGetter={tabSubtitleGetter}
              hideStatusIcons={hideStatusIcons}
              useLegacyStyles={useLegacyStyles}
            />
            {item.subTabs && item.subTabs.length > 0 && (
              <Box className={classes.subListContainer}>
                {item.subTabs.map((subItem) => (
                  <TabSelectorItem
                    key={subItem.value}
                    item={subItem}
                    onSelect={handleTabSelect}
                    selected={selectedTab === subItem.value}
                    tabTitleGetter={tabTitleGetter}
                    tabSubtitleGetter={tabSubtitleGetter}
                    hideStatusIcons={hideStatusIcons}
                    isSubItem
                    useLegacyStyles={useLegacyStyles}
                  />
                ))}
              </Box>
            )}
          </div>
        ))}
      </List>
    </Box>
  );
}

function TabSelectorItem<GenericData extends FieldValues>(
  props: ITabSelectorItemProps<GenericData>
) {
  const {
    item,
    onSelect,
    selected,
    disabled = false,
    tabTitleGetter,
    tabSubtitleGetter,
    hideStatusIcons,
    isSubItem,
    useLegacyStyles,
  } = props;

  const { hasAllPermissions } = usePermissions();

  const { classes, cx, theme } = useStyles({ useLegacyStyles });

  const getIsLocked = () => {
    if (item.requiredPermissions) {
      return !hasAllPermissions(item.requiredPermissions);
    }
    if (disabled) {
      return true;
    }
    return false;
  };

  const handleTabSelect = useCallback(() => {
    onSelect(item);
  }, [item, onSelect]);

  const title = tabTitleGetter ? tabTitleGetter(item) : item.label;
  const subtitle = tabSubtitleGetter ? tabSubtitleGetter(item) : null;

  return (
    <ListItem key={item.value} className={classes.listItem} disablePadding>
      <ListItemButton
        selected={selected}
        onClick={(e) => {
          e.stopPropagation();
          handleTabSelect();
        }}
        className={cx(classes.listItemButton, {
          [classes.subListItem]: isSubItem,
          [classes.disabledItem]: getIsLocked(),
        })}
      >
        <ListItemText
          primary={
            <Typography
              variant="HD2"
              className={classes.tabText}
              fontWeight={
                selected ? theme.fontWeight.bold : theme.fontWeight.regular
              }
            >
              {title}
            </Typography>
          }
          sx={{ margin: 0 }}
          secondary={
            subtitle && <Typography variant="caption">{subtitle}</Typography>
          }
        />
        <div className={classes.statusIconsContainer}>
          {!hideStatusIcons && (
            <VerticalTabStatusIcon
              hasErrors={item.hasErrors}
              isComplete={item.isComplete}
              isLocked={getIsLocked()}
              selected={selected}
            />
          )}
        </div>
      </ListItemButton>
    </ListItem>
  );
}

interface IVerticalTabSelectorProps<GenericData extends FieldValues>
  extends ISharedVerticalTabProps<GenericData> {
  tabs: IVerticalTabProps<GenericData>[];
  selectedTab: string | null | number;
  onTabSelect: (item: IVerticalTabProps<GenericData> | null) => void;
}

interface ITabSelectorItemProps<GenericData extends FieldValues>
  extends ISharedVerticalTabProps<GenericData> {
  item: IVerticalTabProps<GenericData>;
  onSelect: (item: IVerticalTabProps<GenericData>) => void;
  selected: boolean;
  disabled?: boolean;
  isSubItem?: boolean;
  useLegacyStyles?: boolean;
}

interface ISharedVerticalTabProps<GenericData extends FieldValues> {
  tabTitleGetter?: (item: IVerticalTabProps<GenericData>) => string;
  tabSubtitleGetter?: (item: IVerticalTabProps<GenericData>) => string;
  hideStatusIcons?: boolean;
  useLegacyStyles?: boolean;
}

const useStyles = makeStyles<{
  useLegacyStyles?: boolean;
}>()((theme, { useLegacyStyles = false }) => ({
  filterContainer: {
    height: "100%",
    marginTop: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    overflow: "auto",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    padding: 0,
    gap: theme.spacing(1),
    minWidth: 209,
    width: 209,
    marginBottom: theme.spacing(5),
  },
  listItem: {
    padding: theme.spacing(0),
    backgroundColor: "transparent",
    borderRadius: theme.spacing(1),
  },
  statusIconsContainer: {
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
  },
  tabText: {
    overflowWrap: "break-word",
  },
  subTabText: {
    fontSize: theme.typography.body1.fontSize,
  },
  disabledItem: {
    opacity: 0.5,
  },
  listItemButton: {
    padding: theme.spacing(2),
    gap: theme.spacing(1),
    transition: "background-color,border-radius,border-width 0.2s ease",
    borderWidth: 1,
    borderColor: "transparent",
    borderStyle: "solid",
    "&:hover": {
      backgroundColor: theme.palette.grey[300],
      ...(useLegacyStyles
        ? {
            borderTopLeftRadius: theme.spacing(2),
            borderBottomLeftRadius: theme.spacing(2),
          }
        : {
            borderRadius: theme.spacing(2),
          }),
    },
    "&.Mui-selected": {
      ...(useLegacyStyles
        ? {
            backgroundColor: theme.palette.common.white,
            borderTopLeftRadius: theme.spacing(1),
            borderBottomLeftRadius: theme.spacing(1),
          }
        : {
            backgroundColor: theme.palette.primary.light,
            borderColor: theme.palette.primary.accent,
            borderRadius: theme.spacing(1),
            color: theme.palette.primary.main,
          }),
    },
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  subListContainer: {
    display: "flex",
    flexDirection: "column",
    width: "90%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    marginRight: 0,
    marginLeft: "auto",
    gap: useLegacyStyles ? theme.spacing(2) : 0,
  },
  subListItem: {
    ...(useLegacyStyles
      ? {}
      : {
          padding: theme.spacing(2, 4, 2, 2),
          borderLeft: `1px solid ${theme.palette.grey[400]}`,
          margin: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[300],
            ...(useLegacyStyles
              ? {
                  borderTopLeftRadius: theme.spacing(2),
                  borderBottomLeftRadius: theme.spacing(2),
                }
              : {
                  borderRadius: 0,
                }),
          },
          "&.Mui-selected": {
            borderColor: "transparent",
            borderLeft: `1px solid ${theme.palette.primary.accent}`,
            borderRadius: 0,
          },
        }),
  },
}));
